'use client';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/site/ui';
import { cn } from '@/lib/utils';

/* Lightweight toast system (no external dep). A global <ToastProvider> mounts the
   stack; useToast() fires them. Right-aligned on desktop, full-width top on
   mobile, and above dialogs (z-[60] > the z-50 overlays). */

type ToastType = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  title?: string;
  icon?: string;
}

export interface ToastOptions {
  title?: string;
  icon?: string;
  /** ms before auto-dismiss (default 5000). 0 = sticky: stays until the user
   *  closes it — for content that must not vanish (e.g. a one-time password). */
  duration?: number;
}

interface ToastCtx {
  toast: (message: string, type?: ToastType, options?: ToastOptions) => void;
}

const Ctx = React.createContext<ToastCtx | null>(null);

export function useToast(): ToastCtx {
  const c = React.useContext(Ctx);
  if (!c) throw new Error('useToast must be used within <ToastProvider>');
  return c;
}

const TONE: Record<ToastType, string> = {
  success: 'border-[#b7e2c7] bg-success-soft text-success-soft-foreground',
  error: 'border-[#f5c2c2] bg-destructive-soft text-destructive-soft-foreground',
  info: 'border-border bg-white text-foreground',
};
const ICON: Record<ToastType, string> = {
  success: 'checkmark-circle-02',
  error: 'alert-02',
  info: 'information-circle',
};

let seq = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations('common');
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const remove = React.useCallback(
    (id: number) => setItems((x) => x.filter((t) => t.id !== id)),
    [],
  );
  const toast = React.useCallback<ToastCtx['toast']>(
    (message, type = 'info', options = {}) => {
      const id = ++seq;
      setItems((x) => [...x, { id, type, message, title: options.title, icon: options.icon }]);
      const duration = options.duration ?? 5000;
      if (duration > 0) setTimeout(() => remove(id), duration);
    },
    [remove],
  );

  return (
    <Ctx.Provider value={{ toast }}>
      {children}
      {/* One persistent live region on the container — SRs announce toasts as
          they're appended. role=status on each toast-as-it-mounts is unreliable. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-4 top-4 z-[60] flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:w-96 sm:items-end"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'pointer-events-auto flex w-full items-start gap-2.5 rounded-xl border px-4 py-3 shadow-lg',
              TONE[item.type],
            )}
          >
            <Icon name={item.icon ?? ICON[item.type]} size={18} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              {item.title && <p className="text-sm font-semibold text-foreground">{item.title}</p>}
              <p className={cn('text-sm', item.title ? 'mt-0.5 leading-snug' : 'font-medium')}>
                {item.message}
              </p>
            </div>
            <button
              type="button"
              aria-label={t('actions.close')}
              onClick={() => remove(item.id)}
              className="shrink-0 opacity-70 transition-opacity hover:opacity-100"
            >
              <Icon name="cancel-01" size={16} />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
